/**
 * A debug session ties every trace and log a browser tab produces to one id,
 * so a bug report can link to all of them in Datadog. The id is minted by
 * Feature-Flagify extension: it sends it as
 * a request header on everything the tab requests, and stamps it into the
 * page's sessionStorage so the browser logs carry it too. tadarida-api tags
 * its traces and logs with the same attribute and force-keeps them.
 */

/** gRPC metadata and HTTP header the id travels in; gRPC lowercases it anyway */
export const DEBUG_SESSION_ID_HEADER = 'x-ay-debug-session-id';

/** where the page finds its own id; per tab and origin, survives reloads */
export const DEBUG_SESSION_ID_STORAGE_KEY = 'ay-debug-session-id';

/** span tag and log attribute, `@debug.session_id` in Datadog */
export const DEBUG_SESSION_ID_ATTRIBUTE = 'debug.session_id';

/**
 * The id in the canonical lowercase dashed form tadarida-api normalises to,
 * so both sides tag the same value. Undefined for anything but a UUID.
 *
 * Accepts the forms Go's `uuid.Parse` accepts, which is what tadarida-api
 * uses: with dashes, without, in braces, or as a URN.
 *
 * Self-contained, the browser logs script inlines it with `toString()`.
 */
export const parseDebugSessionId = function (value: string | null | undefined) {
  const match = value
    ? /^(?:urn:uuid:)?\{?([0-9a-f]{8})-?([0-9a-f]{4})-?([0-9a-f]{4})-?([0-9a-f]{4})-?([0-9a-f]{12})\}?$/i.exec(
        value,
      )
    : null;
  return match ? match.slice(1).join('-').toLowerCase() : undefined;
};

let cached: string | undefined;

/** the tab's debug session id, if a debugging tool stamped one; browser only */
export const getDebugSessionId = (): string | undefined => {
  if (!process.browser) {
    return undefined;
  }
  // only a missing id is read again: the tool stamps it once per page load,
  // possibly after the first log went out
  if (!cached) {
    try {
      cached = parseDebugSessionId(
        window.sessionStorage.getItem(DEBUG_SESSION_ID_STORAGE_KEY),
      );
    } catch (e) {
      return undefined;
    }
  }
  return cached;
};
